import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../utils/LanguageContext';
import { Copy, Check, RotateCw } from 'lucide-react';

const QueryTips = () => {
  const { t } = useTranslation();
  const { language } = useTranslation();
  const [questions, setQuestions] = useState([]);
  const [selectedTips, setSelectedTips] = useState([]);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadQuestions = async () => {
      setLoading(true);
      try {
        const response = await fetch('/digital_horizon_course_common_questions.json');
        if (!response.ok) throw new Error('Failed to load questions');
        const data = await response.json();
        const languageQuestions = data[language]?.issues || [];
        setQuestions(languageQuestions);
        generateNewTips(languageQuestions);
        setError(null);
      } catch (error) {
        console.error('Error loading questions:', error);
        setError('Failed to load example questions');
      } finally {
        setLoading(false);
      }
    };
    loadQuestions();
  }, [language]);

  const generateNewTips = (questionsData = questions) => {
    if (!questionsData?.length) return;
    const shuffled = [...questionsData].sort(() => 0.5 - Math.random());
    setSelectedTips(shuffled.slice(0, 3));
  };

  const handleCopy = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="animate-pulse grid grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 bg-gray-100 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error || !selectedTips.length) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      <button
        onClick={() => generateNewTips()}
        className="flex items-center gap-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-full mb-4 mx-auto transition-colors"
      >
        <RotateCw className="w-4 h-4" />
        {t('queryPage.showDifferentExamples')}
      </button>
      
      <div className="grid grid-cols-3 gap-4">
        {selectedTips.map((tip, index) => (
          <div
            key={index}
            className="bg-gray-50 rounded p-3 relative group cursor-pointer hover:bg-gray-100 transition-colors"
            onClick={() => handleCopy(tip.question, index)}
          >
            <p className="text-sm text-gray-700 pr-8 line-clamp-3">{tip.question}</p>
            <div className="absolute right-2 top-2 text-gray-400">
              {copiedIndex === index ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default QueryTips;